/// <reference types="google.maps" />
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps, getGoogleMapsKey } from '@/lib/google-maps';

export interface ParsedAddress {
  houseNumber: string;
  line1: string;
  line2: string;
  town: string;
  county: string;
  postcode: string;
}

const getComponent = (
  components: google.maps.places.AddressComponent[] | undefined,
  type: string,
): string => components?.find((c) => c.types.includes(type))?.longText ?? '';

const toParsedAddress = (place: google.maps.places.Place): ParsedAddress => {
  const components = place.addressComponents ?? undefined;
  const streetNumber = getComponent(components, 'street_number');
  const route = getComponent(components, 'route');
  const subpremise = getComponent(components, 'subpremise');
  const premise = getComponent(components, 'premise');
  const locality = getComponent(components, 'locality');
  const postalTown = getComponent(components, 'postal_town');
  // Standard street address: street_number lives in its own field, route is
  // line 1. Rural / named-building addresses (no route) put the premise into
  // line 1 instead of houseNumber so the user sees "4 The Mews" where they
  // expect it.
  const houseNumber = streetNumber;
  const line1 = route || premise;
  // Many UK villages return a `locality` (the village) distinct from the
  // `postal_town` (the larger sorting town). Place that locality on line 2 so
  // it isn't lost.
  const line2 =
    subpremise || (locality && locality !== postalTown ? locality : '');
  const town =
    postalTown ||
    locality ||
    getComponent(components, 'administrative_area_level_3');
  const county =
    getComponent(components, 'administrative_area_level_2') ||
    getComponent(components, 'administrative_area_level_1');
  const postcode = getComponent(components, 'postal_code');
  return { houseNumber, line1, line2, town, county, postcode };
};

interface Props {
  label?: string;
  onSelect: (addr: ParsedAddress) => void;
  className?: string;
}

export const AddressAutocomplete = ({
  label = 'Search your address',
  onSelect,
  className = '',
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getGoogleMapsKey()) {
      setError('Address search is unavailable. Please type your address manually below.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    let element: google.maps.places.PlaceAutocompleteElement | null = null;

    const init = async () => {
      try {
        const maps = await loadGoogleMaps();
        // Idempotent — resolves immediately if the places library is already loaded via the
        // libraries=places URL param.
        await maps.importLibrary('places');
        if (cancelled || !containerRef.current) return;

        element = new maps.places.PlaceAutocompleteElement({
          includedRegionCodes: ['gb'],
        });
        element.placeholder = 'Start typing your address or postcode';
        element.addEventListener('gmp-select', async (event) => {
          try {
            const place = event.placePrediction.toPlace();
            await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
            onSelect(toParsedAddress(place));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch selected place:', err);
          }
        });
        element.addEventListener('gmp-error', () => {
          setError('Address search failed. Please type manually below.');
        });
        containerRef.current.appendChild(element);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Address search unavailable');
          setLoading(false);
        }
      }
    };

    void init();

    return () => {
      cancelled = true;
      element?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={className}>
      <label className="lbl">{label}</label>
      <div ref={containerRef} className="pgs-autocomplete-host" />
      {loading && !error && (
        <p className="mt-1 text-[11px] text-gray-500">Loading address search…</p>
      )}
      {error && <p className="mt-1 text-[11px] text-amber-700">{error}</p>}
      {!loading && !error && (
        <p className="mt-1 text-[11px] text-gray-500">
          Powered by Google — pick your address from the suggestions, or type manually below.
        </p>
      )}
    </div>
  );
};
